import { Component } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { FrequencyKeys } from 'src/app/product-capture/model/consts/frequency.const';
import { ACCRUAL_TYPE_ENUM } from 'src/app/product-capture/model/enums/accrual-type.enum';
import {
  KeyDatesEnum,
  ParticipationBaseBarrierEnum,
  ParticipationDownsideBarrierEnum,
  ParticipationEnum,
  PayoffEnum,
  ScheduleType,
} from 'src/app/product-capture/model/enums/product-capture.enums';
import { ROLL_CONVENTION_ENUM } from 'src/app/product-capture/model/enums/roll-convention.enum';
import { ScheduleFormUtils } from 'src/app/product-capture/utils/schedule.utils';
import { ScheduleComponent } from '../schedule.component';

const participationScheduleProperties = [
  ParticipationEnum.base,
  ParticipationEnum.upside,
  ParticipationEnum.downside,
] as const;

export type ParticipationScheduleProperty =
  typeof participationScheduleProperties[number];

@Component({
  selector: 'app-participation-schedule',
  template: '',
  providers: [
    { provide: ScheduleComponent, useExisting: ParticipationScheduleComponent },
  ],
})
export class ParticipationScheduleComponent extends ScheduleComponent {
  key = ScheduleType.PARTICIPATION;

  schedulePayoffKey = PayoffEnum.participation;

  ngOnInit(): void {
    this.featureForm.valueChanges
      ?.pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(),
        debounceTime(100)
      )
      .subscribe(() => {
        participationScheduleProperties.forEach((field) => {
          const fieldForm = this.featureForm.get(field);
          let schedule = null;

          if (fieldForm?.value) {
            schedule = fieldForm.value.schedule;
            this.scheduleForm.removeControl(field);
            this.buildForm(field);

            schedule?.forEach((row: any) => {
              const fieldForm = this.scheduleForm.get(field);
              const fieldScheduleForm = fieldForm?.get(
                ParticipationEnum.schedule
              ) as FormArray;
              fieldScheduleForm.push(this.newSchedule(row));
            });
          }
        });
      });
  }

  buildForm(field: ParticipationScheduleProperty): void {
    const form = ScheduleFormUtils.createDefaultScheduleForm(this.key);
    form.patchValue({ type: 'NONE' });
    this.scheduleForm.addControl(field, form);

    const subScheduleValue = this.featureForm.get(field)?.value;
    if (!subScheduleValue) {
      return;
    }

    // Downside schedule is a special case, as it has a different type
    const { type: subtype, downsideType, barrier } = subScheduleValue;

    const type = subtype || downsideType;
    if (!type || type === 'NONE') {
      return;
    }

    form.patchValue({
      type,
      scheduleType: 'INDEPENDENT',
      frequency: FrequencyKeys.AT_MATURITY,
      pmtScheduleLag: '0B', // Participation is hidden in the UI, this is the default value
      pmtScheduleLagBase: ACCRUAL_TYPE_ENUM.ADJUSTED,
      pmtRollConvention: ROLL_CONVENTION_ENUM.FOLLOWING,
    });

    form.addControl(ParticipationEnum.schedule, this.fb.array([]));

    const obsRollConvention = this.getRollConvention(
      KeyDatesEnum.tradingRollConvention
    );
    const performance = this.fb.group({
      obsScheduleLag: '0B',
      obsScheduleLagBase: ACCRUAL_TYPE_ENUM.PAYMENT_ADJUSTED,
      obsRollConvention,
      fixFrequency: 'INITIAL_FIXING_DATE',
      obsFrequency: 'PERIOD_END',
    });

    form.addControl('performance', performance);

    // We check the barrier `type` field for coupon and callability
    // but cannot check the same for participation sections (downside, upside, base)
    // since they don't have a `type` field visible on the UI, the type is inferred to be FLAT by default
    if (barrier?.knockInBarrier || barrier?.level) {
      const { obsFrequency } = barrier;

      const barrierGroup: FormGroup = this.fb.group<any>({
        obsScheduleLag: '0B',
        obsScheduleLagBase: ACCRUAL_TYPE_ENUM.PAYMENT_ADJUSTED,
        obsRollConvention,
        obsFrequency,
        fixFrequency: 'INITIAL_FIXING_DATE',
      });

      form.addControl('barrier', barrierGroup);
    }
  }

  getSchedulePayload() {
    const keyDates = ScheduleFormUtils.keyDatesForGetSchedulesPayload(
      this.summary
    );
    const obsRollConvention = this.getRollConvention(
      KeyDatesEnum.tradingRollConvention
    );

    const participation = participationScheduleProperties.reduce(
      (acc, field) => {
        let value = this.scheduleForm.get(field)?.value;

        if (!value || value.type === 'NONE') {
          acc[field] = { type: 'NONE' };
          return acc;
        }

        value = this.scheduleForm.get(field)?.value;
        const featureValue = this.featureForm.get(field)?.value;

        let { barrier } = featureValue;

        if (barrier) {
          if (field === ParticipationEnum.downside) {
            const hasBarrier =
              barrier?.[ParticipationDownsideBarrierEnum.knockInBarrier] ||
              barrier?.[ParticipationDownsideBarrierEnum.obsFrequency];

            if (!hasBarrier) {
              barrier = null;
            }
          } else {
            const barrierLevel = barrier[ParticipationBaseBarrierEnum.level];
            const hasBarrierLevel =
              barrierLevel &&
              Object.keys(barrierLevel).some((key) => barrierLevel[key]);

            const hasBarrier =
              hasBarrierLevel ||
              barrier[ParticipationBaseBarrierEnum.obsFrequency];

            if (!hasBarrier) {
              barrier = null;
            }
          }
        }

        const { schedule, ...payload } = value;
        acc[field] = {
          ...payload,
          performance: {
            ...payload.performance,
            obsRollConvention,
          },
          ...(barrier && {
            barrier: {
              ...payload.barrier,
              obsFrequency: barrier.obsFrequency,
              obsRollConvention,
            },
          }),
        };

        return acc;
      },
      {}
    );

    return participation;
  }

  // No defaulting for participation schedules
  applyDefaultScheduleType(): void {
    return;
  }

  getColumnsByScheduleType(): {} {
    return {
      barrierLevel: false,
      unadjObsDate: true,
      adjObsDate: true,
      unadjPmtDate: false,
      rateDeterminationDate: false,
      accrualStartDate: false,
      accrualEndDate: false,
    };
  }
}
